import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { handleUnAuthorizeError } from '../Utils';
import * as Sentry from '@sentry/react';

const getBaseUrl = () => {
  return process.env.REACT_APP_API_BASE_URL;
};

const AUTH_URL = `${getBaseUrl()}/auth`;
const BASE_URL = getBaseUrl();
const timeout = process.env.NODE_ENV === 'development' ? 300000 : 180000; // 3 min in production

const api = axios.create({
  timeout,
  headers: { 'Content-Type': 'application/json', Accept: '*/*' },
  withCredentials: true, // Required for CloudFront signed cookies to be set and sent
  responseType: 'json',
  baseURL: BASE_URL,
});

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    Sentry.captureException(error);
    return Promise.reject(error);
  },
);

// Single in-flight refresh: concurrent 401s share one /refresh-token call,
// so the backend's delete-then-create rotation can't race against itself.
let refreshInFlight: Promise<number | undefined> | null = null;
const refreshOnce = () => {
  if (!refreshInFlight) {
    refreshInFlight = handleUnAuthorizeError(() => {
      localStorage.clear();
      window.location.replace('/');
    }).finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

// Response interceptor to refresh token on 401
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    if (error.response?.status !== 401) {
      Sentry.captureException(error, {
        extra: {
          url: error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          data: error.response?.data,
        },
      });
    }
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      const status = await refreshOnce();

      if (status === 200) {
        const token = localStorage.getItem('token');
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        originalRequest.headers['Authorization'] = `Bearer ${token}`;
        return api(originalRequest);
      }
    }
    return Promise.reject(error);
  },
);

export const refreshToken = async (refresh: string | null) => {
  try {
    return await post(`${AUTH_URL}/refresh-token`, { refresh }, false);
  } catch (error: any) {
    return error.response;
  }
};

const tokenHeaders = async (useToken: boolean) => {
  const token = localStorage.getItem('token');
  return useToken && token ? { headers: { Authorization: `${token}` } } : {};
};

const uploadTokenHeaders = async (useToken: boolean) => {
  const token = localStorage.getItem('token');
  return useToken && token
    ? {
        headers: {
          Authorization: `${token}`,
          'Content-Type': 'multipart/form-data',
        },
      }
    : { headers: { 'Content-Type': 'multipart/form-data' } };
};

export const get = async (url: string, useToken = true) => {
  const headers = await tokenHeaders(useToken);
  return await api.get(url, headers);
};

export const downloadFile = async (url: string, useToken = true) => {
  const headers = await tokenHeaders(useToken);
  return await api.get(url, { ...headers, responseType: 'blob' });
};

export const post = async (url: string, data: any, useToken = true) => {
  const headers = await tokenHeaders(useToken);
  return await api.post(url, data, headers);
};

export const upload = async (url: string, data: any, useToken = true) => {
  const headers = await uploadTokenHeaders(useToken);
  return await api.post(url, data, headers);
};

export const put = async (url: string, data: any, useToken = true) => {
  const headers = await tokenHeaders(useToken);
  return await api.put(url, data, headers);
};

export const remove = async (url: string, useToken = true) => {
  const headers = await tokenHeaders(useToken);
  return await api.delete(url, headers);
};

export const patch = async (url: string, data: any, useToken = true) => {
  const headers = await tokenHeaders(useToken);
  return await api.patch(url, data, headers);
};

export const patchUpload = async (url: string, data: any, useToken = true) => {
  const headers = await uploadTokenHeaders(useToken);
  return await api.patch(url, data, headers);
};
